/**
 * Attachments section for a wiki page. Sits below the page body.
 *
 * Server contracts:
 *   GET    /pages/{page_id}/attachments       -> PageAttachment[]
 *   POST   /pages/{page_id}/attachments       -> PageAttachment   (multipart `file`)
 *   GET    /attachments/{id}/download         -> { url, expires_in_seconds }
 *   DELETE /attachments/{id}                  -> 204
 *
 * Permissions are enforced server-side. Anyone in the tenant can upload +
 * download; only the uploader OR a tenant admin can delete. The UI hides the
 * Remove button when the caller can't perform the action — keeps the
 * affordance honest.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type CSSProperties } from "react";

import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";

type PageAttachment = {
  id: string;
  page_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by_id: string | null;
  created_at: string;
};

const MAX_BYTES = 25 * 1024 * 1024;
const ADMIN_ROLES = new Set(["admin"]);

export function PageAttachments({ pageId }: { pageId: string }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));
  const [error, setError] = useState<string | null>(null);

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: ["page", pageId, "attachments"],
    queryFn: async () =>
      (await api.get<PageAttachment[]>(`/pages/${pageId}/attachments`)).data,
    enabled: !!pageId,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file, file.name);
      // Don't set Content-Type — axios derives it with the correct
      // multipart boundary. Setting it manually breaks the upload.
      return (
        await api.post<PageAttachment>(`/pages/${pageId}/attachments`, form)
      ).data;
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["page", pageId, "attachments"] });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Upload failed — try again.";
      setError(detail);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/attachments/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["page", pageId, "attachments"] }),
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not remove this attachment.";
      setError(detail);
    },
  });

  const onChosen = async (file: File) => {
    setError(null);
    if (file.size === 0) {
      setError("File is empty.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(
        `File exceeds 25 MB (was ${(file.size / (1024 * 1024)).toFixed(1)} MB).`,
      );
      return;
    }
    upload.mutate(file);
  };

  const onDownload = async (id: string, filename: string) => {
    try {
      const { data } = await api.get<{ url: string }>(
        `/attachments/${id}/download`,
      );
      // Force a download with a sensible filename. Opening in a new tab
      // means PDFs/images preview inline; the presigned response also
      // carries Content-Disposition=attachment from the backend.
      const a = document.createElement("a");
      a.href = data.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Could not open download link.");
    }
  };

  return (
    <section style={section}>
      <header style={sectionHeader}>
        <h2 style={sectionTitle}>
          Attachments {attachments.length > 0 && (
            <span style={{ color: "var(--mute)", fontWeight: 400 }}>
              · {attachments.length}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={upload.isPending}
          style={{
            ...primaryBtn,
            opacity: upload.isPending ? 0.6 : 1,
            cursor: upload.isPending ? "wait" : "pointer",
          }}
        >
          {upload.isPending ? "Uploading…" : "+ Add file"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onChosen(f);
          }}
        />
      </header>

      {error && (
        <p role="alert" style={errorBanner}>
          {error}
        </p>
      )}

      {isLoading ? (
        <p style={{ color: "var(--mute)", fontSize: 13 }}>Loading…</p>
      ) : attachments.length === 0 ? (
        <p style={{ color: "var(--mute)", fontSize: 13, fontStyle: "italic" }}>
          No attachments yet. Drop a PDF, image, spreadsheet, or any file up to 25 MB.
        </p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {attachments.map((a) => {
            const canDelete = isAdmin || a.uploaded_by_id === currentUserId;
            return (
              <li key={a.id} style={row}>
                <span style={fileIcon} aria-hidden>
                  ▤
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--ink)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {a.filename}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--mute)" }}>
                    {formatBytes(a.size_bytes)} · uploaded{" "}
                    {new Date(a.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void onDownload(a.id, a.filename)}
                  style={ghostBtn}
                >
                  Download
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(a.id)}
                    disabled={remove.isPending}
                    style={dangerGhostBtn}
                    title="Remove this attachment"
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------- styles ----------

const section: CSSProperties = {
  marginTop: 48,
  paddingTop: 24,
  borderTop: "1px solid var(--hairline)",
};

const sectionHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 16,
};

const sectionTitle: CSSProperties = {
  flex: 1,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--body-mid)",
  margin: 0,
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "6px 14px",
  fontSize: 12.5,
  fontWeight: 500,
  cursor: "pointer",
};

const ghostBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
  flexShrink: 0,
};

const dangerGhostBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(193,25,48,0.35)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  color: "#c11930",
  cursor: "pointer",
  flexShrink: 0,
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 12px",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  marginBottom: 6,
  background: "var(--canvas)",
};

const fileIcon: CSSProperties = {
  fontSize: 16,
  color: "var(--mute)",
  width: 18,
  textAlign: "center",
  flexShrink: 0,
};

const errorBanner: CSSProperties = {
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
  padding: "8px 12px",
  margin: "0 0 12px",
};
